import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import BlockIcon from '@mui/icons-material/Block';
import type { GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetInvitationsDocument,
  GetCurrentTenantDocument,
  GetOrganizationsForAddressListDocument,
  ResendInvitationDocument,
  RevokeInvitationDocument,
  MarkInvitationFailedDocument,
  type GetInvitationsQuery,
  type GetCurrentTenantQuery,
  type GetOrganizationsForAddressListQuery,
  type InvitationFieldsFragment,
} from '@/generated/root-types';
import { SendMailDocument } from '@/generated/pos-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import { errorsFromPayload } from '@/utils/mutationErrors';
import { invitationStateColors, translateInvitationState } from '@/utils/billingStateLabels';
import {
  buildInviteContactBlock,
  buildRegistrationLink,
  contactToForm,
  CUSTOMER_INVITE_EMAIL_TEMPLATE,
  dataUriToInlineLogoAttachment,
  DEFAULT_ORDA_LOGO_SRC,
  pickCustomerContactAddress,
} from '@/components/customer-invite/customerInviteUtils';

interface InvitationRow {
  id: string;
  recipientEmail: string;
  recipientName: string;
  message: string;
  state: string;
  createdAt: string;
  respondedAt: string;
  raw: InvitationFieldsFragment;
  [key: string]: unknown;
}

/** Invitations may be resent/revoked while pending or after a failed send. */
function isActionable(state: string): boolean {
  return state === 'SENT' || state === 'FAILED';
}

export default function Invitations() {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<InvitationFieldsFragment | null>(null);
  const [revoking, setRevoking] = useState(false);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' }) : '–';

  const { data, loading, refetch } = useQuery<GetInvitationsQuery>(GetInvitationsDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });

  const { data: tenantData } = useQuery<GetCurrentTenantQuery>(GetCurrentTenantDocument);
  const tenantId = tenantData?.currentTenant?.id;
  const resellerLogo = tenantData?.currentTenant?.resellerLogo ?? null;

  const { data: orgsData } = useQuery<GetOrganizationsForAddressListQuery>(
    GetOrganizationsForAddressListDocument,
    { variables: { first: GRAPHQL_MAX_PAGE_SIZE, tenantId: tenantId ?? null }, skip: !tenantId },
  );
  const organizations = orgsData?.organizations?.nodes ?? [];
  const activeOrg = organizations.find((o) => o.isDefault) ?? organizations[0] ?? null;
  const contactAddress = pickCustomerContactAddress(activeOrg?.addresses);

  const [resendInvitation] = useMutation(ResendInvitationDocument);
  const [revokeInvitation] = useMutation(RevokeInvitationDocument);
  const [markInvitationFailed] = useMutation(MarkInvitationFailedDocument);
  const [sendMail] = useMutation(SendMailDocument);

  const invitations = data?.invitations?.nodes ?? [];
  const totalCount = data?.invitations?.totalCount ?? 0;

  const rows: InvitationRow[] = invitations.map((i) => ({
    id: i.id,
    recipientEmail: i.recipientEmail,
    recipientName: i.recipientName ?? '–',
    message: i.message ?? t('invitations.noMessage'),
    state: i.state,
    createdAt: formatDate(i.createdAt),
    respondedAt: formatDate(i.respondedAt),
    raw: i,
  }));

  const handleResend = async (invitation: InvitationFieldsFragment) => {
    if (!tenantId || !contactAddress) {
      showError(t('customers.invite.noContactBanner'));
      return;
    }
    const logoAttachment = dataUriToInlineLogoAttachment(resellerLogo || DEFAULT_ORDA_LOGO_SRC);
    if (!logoAttachment) {
      showError(t('customers.invite.logoInvalidType'));
      return;
    }

    setResendingId(invitation.id);
    try {
      const resendRes = await resendInvitation({ variables: { input: { id: invitation.id } } });
      const resendErr = errorsFromPayload(resendRes.data?.resendInvitation);
      if (resendErr) {
        showError(resendErr);
        return;
      }

      const organizationName = activeOrg?.name ?? '';
      const contactBlock = buildInviteContactBlock({
        organizationName,
        form: contactToForm(contactAddress),
        phoneLabel: t('organizationDetail.phone'),
        emailLabel: t('organizationDetail.email'),
      });
      const inviteLink = buildRegistrationLink(tenantId, invitation.token);

      const mailRes = await sendMail({
        variables: {
          sendMail: {
            to: [invitation.recipientEmail],
            subject: t('customers.invite.emailSubject', { organizationName }),
            templateName: CUSTOMER_INVITE_EMAIL_TEMPLATE,
            isBodyHtml: true,
            bodyMappings: [
              { key: '#TITLE#', value: t('customers.invite.emailTitle') },
              { key: '#HEADING#', value: t('customers.invite.emailHeading', { organizationName }) },
              { key: '#SUBTITLE#', value: t('customers.invite.emailSubtitle') },
              { key: '#REGISTRATION_LINK#', value: inviteLink },
              { key: '#BUTTON_LABEL#', value: t('customers.invite.emailLinkLabel') },
              { key: '#CONTACT_HEADING#', value: t('customers.invite.emailContactHeading') },
              { key: '#CONTACT_BLOCK#', value: contactBlock },
              { key: '#MESSAGE#', value: invitation.message ?? '' },
            ],
            attachments: [
              {
                fileName: logoAttachment.fileName,
                mimeType: logoAttachment.mimeType,
                content: logoAttachment.content,
                contentId: logoAttachment.contentId,
              },
            ],
          },
        },
        context: { schema: 'PosSchema' },
      });

      if (!mailRes.data?.sendMail?.isSuccess) {
        await markInvitationFailed({ variables: { input: { id: invitation.id } } });
        showError(mailRes.data?.sendMail?.message ?? t('invitations.resendFailed'));
        return;
      }

      showSuccess(t('invitations.resent'));
      await refetch();
    } catch (e) {
      await markInvitationFailed({ variables: { input: { id: invitation.id } } });
      showError(e instanceof Error ? e.message : t('invitations.resendFailed'));
    } finally {
      setResendingId(null);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const res = await revokeInvitation({ variables: { input: { id: revokeTarget.id } } });
      const err = errorsFromPayload(res.data?.revokeInvitation);
      if (err) {
        showError(err);
        return;
      }
      showSuccess(t('invitations.revoked'));
      setRevokeTarget(null);
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('invitations.revokeFailed'));
    } finally {
      setRevoking(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'recipientEmail',
      headerName: t('invitations.recipientEmail'),
      flex: 1,
      minWidth: 200,
    },
    { field: 'recipientName', headerName: t('invitations.recipientName'), width: 160 },
    { field: 'message', headerName: t('invitations.message'), flex: 1, minWidth: 180 },
    {
      field: 'state',
      headerName: t('common.status'),
      width: 130,
      renderCell: (params) => (
        <Chip
          label={translateInvitationState(t, String(params.value))}
          size="small"
          color={invitationStateColors[String(params.value)] || 'default'}
        />
      ),
    },
    { field: 'createdAt', headerName: t('common.created'), width: 150 },
    { field: 'respondedAt', headerName: t('invitations.respondedAt'), width: 150 },
    {
      field: 'rowActions',
      headerName: t('common.actions'),
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const invitation = (params.row as InvitationRow).raw;
        if (!isActionable(invitation.state)) return null;
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <Tooltip title={t('invitations.actions.resend')}>
              <IconButton
                size="small"
                disabled={resendingId === invitation.id}
                onClick={() => void handleResend(invitation)}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('invitations.actions.revoke')}>
              <IconButton size="small" onClick={() => setRevokeTarget(invitation)}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('invitations.title')}
      </Typography>
      <DataTable<InvitationRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
      />

      <Dialog open={revokeTarget != null} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>{t('invitations.revokeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('invitations.revokeConfirmMessage', { email: revokeTarget?.recipientEmail })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>{t('common.cancel')}</Button>
          <Button
            onClick={() => void handleRevoke()}
            color="error"
            variant="contained"
            disabled={revoking}
            autoFocus
          >
            {t('invitations.actions.revoke')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
